import Link from "next/link";
import type { Metadata } from "next";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const metadata: Metadata = {
  title: "Privacy Policy — RateRight",
  description:
    "Privacy Policy for RateRight. How we collect, use, and protect your personal information in compliance with Australian privacy law.",
};

export default function PrivacyPage() {
  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Link href="/">
            <Button variant="ghost" size="sm" className="mb-4 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to RateRight
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: 3 March 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Overview</h2>
            <p>
              RateRight (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              personal information when you use the RateRight platform (&quot;Platform&quot;).
            </p>
            <p>
              We comply with the <em>Privacy Act 1988</em> (Cth), including the Australian
              Privacy Principles (APPs), and any applicable state or territory privacy legislation.
            </p>
            <p>
              By using the Platform, you consent to the collection and use of your information
              as described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium mb-2">2.1 Information You Provide</h3>
            <p>When you register and use the Platform, we may collect:</p>

            <h4 className="text-lg font-medium mt-4 mb-2">For Workers:</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>Full name and contact details (phone number, email)</li>
              <li>Location (suburb, city, state)</li>
              <li>Construction trade(s) and skills</li>
              <li>Years of experience and qualifications</li>
              <li>Licence and certification details (e.g., White Card, forklift licence)</li>
              <li>Work availability and preferences</li>
              <li>Voice recordings (when using voice-to-profile feature)</li>
              <li>Profile photo (optional)</li>
              <li>Emergency contact information</li>
              <li>Right to work documentation</li>
            </ul>

            <h4 className="text-lg font-medium mt-4 mb-2">For Hirers (Contractors):</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>Business name, ABN, and contact details</li>
              <li>Job posting details (role, location, dates, requirements)</li>
              <li>Payment information (processed by Stripe — we do not store card details)</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-medium mb-2 mt-6">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device type, browser, and operating system</li>
              <li>IP address and approximate location</li>
              <li>Pages visited, features used, and interaction patterns</li>
              <li>Referral source</li>
              <li>Cookies and similar tracking technologies (see Section 8)</li>
            </ul>

            <h3 className="text-xl font-medium mb-2 mt-6">2.3 Information from Third Parties</h3>
            <p>
              We may receive information from authentication providers (when you sign in via
              Google or other services) and from Stripe regarding payment transaction status.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p>We use your personal information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Operate the Platform</strong> — Create accounts, manage profiles, facilitate matches between Workers and Hirers</li>
              <li><strong>AI-Powered Matching</strong> — Analyse worker skills, experience, and location to generate optimal job matches</li>
              <li><strong>Voice Processing</strong> — Convert voice recordings into structured profile data using AI transcription and processing (recordings are processed and not retained long-term)</li>
              <li><strong>Communications</strong> — Send SMS notifications, match alerts, hire confirmations, and platform updates</li>
              <li><strong>Payments</strong> — Process hiring fees through our payment processor (Stripe)</li>
              <li><strong>Ratings and Reviews</strong> — Display user ratings to maintain platform quality</li>
              <li><strong>Safety and Security</strong> — Detect fraud, enforce our Terms, and protect users</li>
              <li><strong>Improvement</strong> — Analyse usage patterns to improve the Platform and user experience</li>
              <li><strong>Legal Compliance</strong> — Meet obligations under Australian law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. How We Share Your Information</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Other Users</strong> — Workers&apos; profiles (skills, experience, location, ratings) are
                visible to Hirers when matched. Hirer job details are visible to matched Workers. Contact
                details are shared only when a hire is confirmed.
              </li>
              <li>
                <strong>Service Providers</strong> — We use trusted third-party services including:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Supabase (database and authentication hosting — servers in Australia)</li>
                  <li>Stripe (payment processing — PCI DSS compliant)</li>
                  <li>OpenAI (AI matching and voice processing — data processing agreement in place)</li>
                  <li>Resend (transactional email delivery)</li>
                </ul>
              </li>
              <li>
                <strong>Legal Requirements</strong> — When required by law, court order, or to protect
                the rights, property, or safety of RateRight, our users, or the public
              </li>
              <li>
                <strong>Business Transfers</strong> — In connection with a merger, acquisition, or sale
                of assets, with appropriate confidentiality protections
              </li>
            </ul>
            <p className="mt-2">
              We do not sell your personal information to third parties. We do not share your
              data with advertisers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Construction Industry Data</h2>
            <p>
              Given the nature of construction work, we handle certain sensitive information
              with additional care:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Licences and Certifications</strong> — Licence numbers and certification
                details are stored securely and displayed only to relevant parties during the
                matching process. We do not independently verify licence validity.
              </li>
              <li>
                <strong>Work Health and Safety</strong> — We may collect WHS-related information
                (e.g., White Card numbers, safety certifications) to facilitate compliance.
                Hirers remain responsible for all WHS obligations under the <em>Work Health and
                Safety Act 2011</em>.
              </li>
              <li>
                <strong>Location Data</strong> — Worker location is used for proximity-based
                matching. We store suburb/city-level location, not precise GPS coordinates.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Data Storage and Security</h2>
            <p>
              Your data is stored on secure servers hosted by Supabase, with primary databases
              located in the Sydney, Australia region. We implement industry-standard security
              measures including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption in transit (TLS/SSL) and at rest</li>
              <li>Row-level security (RLS) on all database tables</li>
              <li>Secure authentication with session management</li>
              <li>Regular security reviews and updates</li>
              <li>Access controls limiting data access to authorised personnel and systems</li>
            </ul>
            <p className="mt-2">
              While we take reasonable steps to protect your information, no system is completely
              secure. We encourage you to use strong passwords and keep your account credentials
              confidential.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Overseas Disclosure</h2>
            <p>
              Some of our service providers may process data outside Australia (e.g., OpenAI
              for AI processing, Stripe for payments). Before disclosing personal information
              overseas, we take reasonable steps to ensure the recipient complies with the
              APPs or is subject to a substantially similar privacy regime, in accordance with
              APP 8.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking</h2>
            <p>
              We use essential cookies to maintain your session and authentication state.
              We may use analytics cookies to understand how users interact with the Platform.
              You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Your Rights</h2>
            <p>
              Under the Australian Privacy Principles, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access</strong> — Request a copy of the personal information we hold about you (APP 12)</li>
              <li><strong>Correction</strong> — Request correction of inaccurate, incomplete, or outdated information (APP 13)</li>
              <li><strong>Complaint</strong> — Lodge a complaint about our handling of your information</li>
              <li><strong>Opt-out</strong> — Unsubscribe from marketing communications at any time</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us using the details in Section 12. We will
              respond to access and correction requests within 30 days.
            </p>
            <p>
              If you are unsatisfied with our response to a complaint, you may contact the
              Office of the Australian Information Commissioner (OAIC) at{" "}
              <a
                href="https://www.oaic.gov.au"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                www.oaic.gov.au
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as
              needed to provide our services. After account closure, we may retain certain
              information as required by law (e.g., financial records under the{" "}
              <em>Corporations Act 2001</em>) or for legitimate business purposes (e.g., fraud
              prevention, dispute resolution).
            </p>
            <p>
              Voice recordings used for profile creation are processed by our AI systems and
              are not retained after the profile data has been extracted.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Children&apos;s Privacy</h2>
            <p>
              The Platform is not intended for individuals under 18 years of age. We do not
              knowingly collect personal information from children. If we become aware that
              we have collected information from a person under 18, we will take steps to
              delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
            <p>
              For privacy-related enquiries, access requests, or complaints, contact us at:
            </p>
            <ul className="list-none space-y-1 mt-2">
              <li><strong>Privacy Officer:</strong> RateRight</li>
              <li><strong>Email:</strong> privacy@rateright.com.au</li>
              <li><strong>Platform:</strong> <Link href="/report-issue" className="text-primary underline">Report an Issue</Link></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              material changes via email or through the Platform. The &quot;Last updated&quot; date
              at the top of this page indicates when the policy was last revised.
            </p>
          </section>
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row gap-4 items-center justify-between">
          <Link href="/terms">
            <Button variant="outline">Read our Terms of Service</Button>
          </Link>
          <Link href="/">
            <Button variant="ghost">Back to RateRight</Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
